import React from 'react';
import { Trophy, Edit3, Trash2 } from 'lucide-react';
import { formatJudoClassification, JUDO_CLASS_FORMATS } from '../../data/combativesJudo';

/**
 * JudoSessionDetail — read view for a logged Judo session. Triggered from
 * the session card. Mirrors BJJSessionDetail's structural choices but
 * uses Judo's mechanism-based classification rendering.
 */
const JudoSessionDetail = ({ session, onClose, onEdit, onDelete }) => {
  if (!session) return null;
  const classFormatLabel = JUDO_CLASS_FORMATS.find((f) => f.value === session.classFormat)?.label || session.classFormat;
  const techniques = session.techniques || [];
  const r = session.randori || {};
  const reflection = session.reflection || {};

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-enf-surface-deep border border-enf-surface-border sm:rounded-xl flex flex-col max-h-[100vh] sm:max-h-[90vh]" data-testid="judo-session-detail">
        <div className="flex items-center justify-between p-5 border-b border-enf-surface-border flex-shrink-0">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-enf-accent-fg font-semibold mb-1 flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Judo session
            </div>
            <div className="text-sm text-enf-text-secondary">{new Date(session.date).toLocaleDateString()} · {session.duration || '—'} min · {classFormatLabel || '—'}</div>
          </div>
          <div className="flex items-center gap-1.5">
            {onEdit && (
              <button onClick={onEdit} aria-label="Edit" className="p-2 text-enf-text-muted hover:text-enf-text-primary" data-testid="judo-detail-edit">
                <Edit3 className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button onClick={onDelete} aria-label="Delete" className="p-2 text-enf-text-muted hover:text-enf-role-warning" data-testid="judo-detail-delete">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} aria-label="Close" className="p-2 text-enf-text-muted hover:text-enf-text-primary text-xl leading-none" data-testid="judo-detail-close">×</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
          {session.beltSnapshot && <div><span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Belt</span><div className="text-enf-text-primary">{session.beltSnapshot}</div></div>}
          {(session.focusModes || []).length > 0 && (
            <div>
              <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Focus modes</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {session.focusModes.map((m) => <span key={m} className="px-2 py-0.5 bg-enf-surface-mid border border-enf-surface-border rounded text-xs">{m}</span>)}
              </div>
            </div>
          )}
          {(session.coaches || []).length > 0 && (
            <div><span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Coaches</span><div className="text-enf-text-primary">{session.coaches.join(', ')}</div></div>
          )}
          {session.intent && (
            <div><span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Intent</span><div className="text-enf-text-primary whitespace-pre-wrap">{session.intent}</div></div>
          )}

          {techniques.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Techniques · {techniques.length}</div>
              <div className="space-y-2">
                {techniques.map((t, i) => (
                  <div key={i} className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md" data-testid={`judo-detail-technique-${i}`}>
                    <div className="font-semibold text-enf-text-primary">{formatJudoClassification(t.classification) || '—'}</div>
                    <div className="text-xs text-enf-text-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {t.outcome && <span><span className="text-enf-text-muted">Outcome:</span> <span className="text-enf-text-secondary">{t.outcome}</span></span>}
                      {t.reps && <span><span className="text-enf-text-muted">Reps:</span> <span className="text-enf-text-secondary">{t.reps}</span></span>}
                      {t.partnerLevel && <span><span className="text-enf-text-muted">Partner:</span> <span className="text-enf-text-secondary">{t.partnerLevel}</span></span>}
                      <span className="text-enf-role-info">{t.mastery}</span>
                    </div>
                    {t.notes && <div className="text-xs text-enf-text-secondary mt-2 whitespace-pre-wrap border-l-2 border-enf-surface-border pl-2">{t.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(r.rounds > 0 || r.throwsCompleted > 0 || r.throwsCountered > 0 || r.groundworkSubmissions > 0 || r.groundworkSubmitted > 0) && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Randori</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <div><span className="text-enf-text-muted">Rounds:</span> <span className="font-semibold">{r.rounds}</span></div>
                <div><span className="text-enf-text-muted">Throws ✓:</span> <span className="font-semibold">{r.throwsCompleted}</span></div>
                <div><span className="text-enf-text-muted">Throws ✗:</span> <span className="font-semibold">{r.throwsCountered}</span></div>
                <div><span className="text-enf-text-muted">GW subs ✓:</span> <span className="font-semibold">{r.groundworkSubmissions}</span></div>
                <div><span className="text-enf-text-muted">GW subs ✗:</span> <span className="font-semibold">{r.groundworkSubmitted}</span></div>
                {r.feltScale && <div><span className="text-enf-text-muted">Felt:</span> <span className="font-semibold">{r.feltScale}/5</span></div>}
              </div>
              {(r.partners || []).length > 0 && <div className="text-xs text-enf-text-secondary mt-2"><span className="text-enf-text-muted">Partners:</span> {r.partners.join(', ')}</div>}
              {r.notes && <div className="text-xs text-enf-text-secondary mt-1 whitespace-pre-wrap">{r.notes}</div>}
            </div>
          )}

          {(reflection.intentHit || reflection.win || reflection.gap || reflection.nextFocus) && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md space-y-2 text-xs">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Reflection</div>
              {reflection.intentHit && <div><span className="text-enf-text-muted">Intent hit:</span> <span className="font-semibold">{reflection.intentHit}</span></div>}
              {reflection.win && <div><span className="text-enf-text-muted">Win:</span> <span className="text-enf-text-secondary whitespace-pre-wrap">{reflection.win}</span></div>}
              {reflection.gap && <div><span className="text-enf-text-muted">Gap:</span> <span className="text-enf-text-secondary whitespace-pre-wrap">{reflection.gap}</span></div>}
              {reflection.nextFocus && <div><span className="text-enf-text-muted">Next focus:</span> <span className="text-enf-text-secondary whitespace-pre-wrap">{reflection.nextFocus}</span></div>}
            </div>
          )}

          {session.notes && (
            <div><span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Notes</span><div className="text-enf-text-secondary whitespace-pre-wrap">{session.notes}</div></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JudoSessionDetail;
